/* ============================================================
 * frontend-v3/src/screens/schedules/FormatMismatchCell.tsx
 *
 * NMW-416 / NMW-418 — inline format-mismatch affordance for a schedule
 * timeline row.
 *
 * Before this, a row whose video profile disagreed with the rest of the
 * playlist showed a bare "No coincide" label; the only way to ACT on it was
 * to open the validation drawer, fire a re-encode there, then come back to
 * the timeline, open the "Cambiar versión" picker, and hope the variant had
 * landed. Three surfaces for one decision, with no visible progress in
 * between.
 *
 * This cell collapses that into the element that already reports the problem:
 *
 *   mismatch  →  [ Convertir a 1080×1920@30 ]        (requestRetranscode)
 *             →  [ live progress bar · 42% ]         (assetTranscodes row)
 *             →  [ Usar versión ]                    (repinScheduleItem)
 *             →  the row matches; the warning disappears on its own.
 *
 * Honest-state contract: every state below is derived from a real reactive
 * read of `assetTranscodes`. Nothing is optimistic — a conversion is only
 * shown as running because a rendition row says so, and "Usar versión" only
 * appears for a row the backend already marked `ready` with a playbackUrl.
 * Both mutations go through `useMutationWithToast`, so backend throws surface
 * as error toasts and never as a fabricated success.
 *
 * Query scope: the `getAssetTranscodes` subscription is per-OUTLIER-row, not
 * per-row. Outliers are by construction the minority bucket of the playlist
 * (compliance.checkSchedule majority vote), and in the virtualized list only
 * mounted rows subscribe — so this stays a handful of subscriptions, not one
 * per timeline item.
 * ============================================================ */
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import type { Id } from '@convex/dataModel';
import { AccessGate, Btn, Icon, Progress } from '../../components/primitives';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';

export interface ProfileShape {
  width?: number | null;
  height?: number | null;
  frameRate?: number | null;
  audioSampleRate?: number | null;
}

/** The subset of an `assetTranscodes` row this cell reads. */
interface Rendition {
  _id: string;
  status: 'pending' | 'processing' | 'ready' | 'failed';
  profile: ProfileShape & { label?: string };
  playbackUrl?: string;
  progressPercent?: number;
  reason?: string;
  createdAt: number;
}

/** A conversion target, complete enough for `requestRetranscode`. */
interface ConversionTarget {
  width: number;
  height: number;
  frameRate: number;
  audioSampleRate?: number;
}

// Structural run() handles from useMutationWithToast — same pattern as
// ScheduleValidationDrawer, so we don't couple to the wrapper's internal type.
type ConvertHandle = {
  run: (args: {
    assetId: Id<'assets'>;
    reason?: string;
    targetProfile: ConversionTarget;
  }) => Promise<unknown>;
};
type RepinHandle = {
  run: (args: {
    scheduleItemId: Id<'schedule'>;
    transcodeId: Id<'assetTranscodes'>;
  }) => Promise<unknown>;
};

interface FormatMismatchCellProps {
  /** `schedule` row id — the repin target. */
  scheduleItemId: string;
  /** Stringified `assets` id carried by the schedule row. */
  assetId?: string | null;
  /** Human-readable mismatch explanation (rule description — details). */
  reason: string;
  /** The playlist's majority profile — the conversion target. */
  majority: ProfileShape;
  /** Parent-level busy flag (a mutation is already in flight). */
  disabled?: boolean;
}

const WARN = 'var(--warn, #b45309)';

/**
 * A conversion target is only dispatchable when the majority carries all three
 * dims `requestRetranscode` requires (the container's TARGET_* env vars).
 * `audioSampleRate` is carried for provenance only.
 */
function resolveTarget(majority: ProfileShape): ConversionTarget | null {
  if (
    majority?.width == null ||
    majority?.height == null ||
    majority?.frameRate == null
  ) {
    return null;
  }
  return {
    width: majority.width,
    height: majority.height,
    frameRate: majority.frameRate,
    audioSampleRate: majority.audioSampleRate ?? undefined,
  };
}

// Must match compliance.checkSchedule's FPS_EPSILON exactly. It absorbs probe
// representation noise only (29.97 vs 30000/1001) — a delivered 29.97 does NOT
// satisfy a requested 30 target, because that is precisely the mismatch the
// detector flags (mixed cadence → short x9k3 segments → videoIngestionStarved
// on the YouTube push). If this window were the looser one, a conversion that
// came back still-29.97 would be offered as "Usar versión" and the warning
// would reappear the moment the row was repinned.
const FPS_EPSILON = 0.001;

function matchesTarget(
  profile: ProfileShape | undefined,
  target: ConversionTarget,
): boolean {
  if (profile?.width == null || profile?.height == null) return false;
  if (profile.width !== target.width || profile.height !== target.height) {
    return false;
  }
  if (typeof profile.frameRate !== 'number') return false;
  return Math.abs(profile.frameRate - target.frameRate) <= FPS_EPSILON;
}

export function FormatMismatchCell({
  scheduleItemId,
  assetId,
  reason,
  majority,
  disabled,
}: FormatMismatchCellProps) {
  const target = resolveTarget(majority);
  const validAsset = !!assetId && /^[a-z0-9]{20,}$/.test(assetId);

  // Reactive rendition read — the conversion's progress source. Skipped for a
  // legacy/sentinel assetId (e.g. the `__live__` placeholder), which would
  // otherwise throw an ArgumentValidationError past the boundary.
  const data = useQuery(
    api.transcodes.getAssetTranscodes,
    validAsset
      ? { assetId: assetId as unknown as Id<'assets'> }
      : 'skip',
  ) as { activeTranscodeId: string | null; renditions: Rendition[] } | undefined;

  const convertM = useMutationWithToast(api.transcodes.requestRetranscode, {
    logLabel: 'transcodes_requestRetranscode',
    successMessage: 'Conversión iniciada. El progreso aparece aquí mismo.',
    errorMessagePrefix: 'No se pudo iniciar la conversión',
  }) as unknown as ConvertHandle;
  const repinM = useMutationWithToast(api.schedule.repinScheduleItem, {
    logLabel: 'schedule_repinScheduleItem',
    successMessage: 'Versión actualizada.',
    errorMessagePrefix: 'No se pudo cambiar la versión',
  }) as unknown as RepinHandle;

  // Newest-first candidates whose profile matches the conversion target. The
  // in-flight row wins over a ready one so a re-run in progress is what the
  // operator sees; `failed` is only surfaced when nothing better exists.
  const candidates: Rendition[] = target
    ? (data?.renditions ?? [])
        .filter((r) => matchesTarget(r.profile, target))
        .slice()
        .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
    : [];
  const inFlight = candidates.find(
    (r) => r.status === 'pending' || r.status === 'processing',
  );
  const ready = candidates.find((r) => r.status === 'ready' && !!r.playbackUrl);
  const failed = candidates.find((r) => r.status === 'failed');

  const targetLabel = target
    ? `${target.height}×${target.width}@${target.frameRate}`
    : null;

  const handleConvert = () => {
    if (!target || !validAsset) return;
    void convertM
      .run({
        assetId: assetId as unknown as Id<'assets'>,
        reason: 'operator:schedule-format-mismatch',
        targetProfile: target,
      })
      .catch(() => {
        /* honest wrapper already toasted the throw */
      });
  };

  const handleUse = (transcodeId: string) => {
    void repinM
      .run({
        scheduleItemId: scheduleItemId as unknown as Id<'schedule'>,
        transcodeId: transcodeId as unknown as Id<'assetTranscodes'>,
      })
      .catch(() => {
        /* honest wrapper already toasted the throw */
      });
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      <span
        title={reason}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          color: WARN,
          fontSize: 12,
          cursor: 'help',
        }}
      >
        <Icon name="alertTriangle" size={14} color={WARN} />
        No coincide
      </span>

      {/* STATE 1 — converting. Real per-rendition progress, fed by the
          container's transcode_progress callback (convex/http.ts →
          transcodes.updateRenditionProgress). A `pending` row has not been
          picked up yet, so it honestly reads "En cola" rather than 0%. */}
      {inFlight ? (
        <div
          title={`Convirtiendo a ${targetLabel}`}
          aria-label={`Convirtiendo a ${targetLabel}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: 'var(--muted-slate)',
          }}
        >
          <div style={{ width: 72 }}>
            <Progress value={inFlight.progressPercent ?? 0} h={4} />
          </div>
          {inFlight.status === 'pending'
            ? 'En cola'
            : `${Math.round(inFlight.progressPercent ?? 0)}%`}
        </div>
      ) : /* STATE 2 — converted and ready. One click repins this timeline row
             onto the new rendition; the mismatch then resolves itself on the
             next compliance read. */
      ready ? (
        <AccessGate perm="schedule_create">
          <Btn
            variant="ghost"
            tone="good"
            size="sm"
            icon="check"
            disabled={disabled}
            title={`Conversión lista (${targetLabel}). Asignar esta versión al elemento.`}
            onClick={() => handleUse(ready._id)}
          >
            Usar versión
          </Btn>
        </AccessGate>
      ) : /* STATE 3 — the conversion failed. Offer the retry explicitly rather
             than silently showing "Convertir" again as if nothing happened. */
      failed ? (
        <AccessGate perm="asset_update">
          <Btn
            variant="ghost"
            tone="bad"
            size="sm"
            icon="refresh"
            disabled={disabled || !target}
            title={failed.reason ?? 'La conversión anterior falló.'}
            onClick={handleConvert}
          >
            Reintentar
          </Btn>
        </AccessGate>
      ) : /* STATE 4 — nothing dispatched yet. */
      target ? (
        <AccessGate perm="asset_update">
          <Btn
            variant="ghost"
            tone="warn"
            size="sm"
            icon="refresh"
            disabled={disabled || !validAsset}
            title={`Convertir este contenido a ${targetLabel} (el perfil mayoritario de la programación).`}
            onClick={handleConvert}
          >
            Convertir a {targetLabel}
          </Btn>
        </AccessGate>
      ) : (
        /* No dispatchable target — the majority profile is itself incomplete
           (an un-probed legacy asset). Honest about why there is no button. */
        <span
          title="El perfil mayoritario de la programación está incompleto (falta resolución o fps), así que no se puede fijar un objetivo de conversión."
          style={{ fontSize: 12, color: 'var(--muted-slate)', cursor: 'help' }}
        >
          sin objetivo
        </span>
      )}
    </div>
  );
}

export default FormatMismatchCell;
